import { useState, useEffect, useRef } from 'react';
import {
  documentsAPI,
  Document,
  DocumentCategory,
  CategoryWithDocuments,
  DocumentStats,
} from '../../lib/api';
import { toast } from 'sonner';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { usePagination, useModal } from '../../hooks';
import { getErrorMessage } from '../../hooks/useFetchData';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { externalNavConfirmConfig } from '../../lib/externalUrl';
import { ViewMode, UploadFormData, INITIAL_UPLOAD_FORM, DocumentFilters, INITIAL_FILTERS, DocumentVisibility, DocumentSide } from './types';
import { useView } from '../../contexts/ViewContext';

export function useDocuments(side: DocumentSide = 'customer') {
  const confirm = useConfirm();
  const { viewKey, getViewQueryParams } = useView();
  const [viewMode, setViewMode] = useState<ViewMode>('folder');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState<DocumentStats | null>(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<DocumentFilters>(INITIAL_FILTERS);

  // Inline PDF preview (rendered via pdf.js in PdfPreviewModal). Holds the
  // short-lived signed URL that streams the bytes.
  const [pdfPreview, setPdfPreview] = useState<{ open: boolean; fileUrl: string | null; title: string }>({
    open: false, fileUrl: null, title: '',
  });

  // Folder view data
  const [categoriesWithDocs, setCategoriesWithDocs] = useState<CategoryWithDocuments[]>([]);

  // List view data
  const [documents, setDocuments] = useState<Document[]>([]);
  const pagination = usePagination({ pageSize: 20 });
  const uploadDialog = useModal();
  const editDialog = useModal();
  // CR-5330: Document details popup (metadata + audit history + file
  // actions). Mirrors the Delivery / Purchasing detail modals.
  const detailDialog = useModal();
  const [detailDoc, setDetailDoc] = useState<Document | null>(null);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  // CR-5325: Edit form mirrors the upload form — tags, version, three-way
  // visibility (private/shared/public) + share list. We keep document_type
  // and file replacement off the form for now (see EditDialog comment).
  // CR-5329: edit form also exposes the Read & Accept toggle.
  const [editForm, setEditForm] = useState<{
    id: number;
    title: string;
    description: string;
    category: string;
    tags: string;
    version: string;
    visibility: DocumentVisibility;
    shared_with_company_ids: number[];
    requires_acceptance: boolean;
  }>({
    id: 0,
    title: '',
    description: '',
    category: '',
    tags: '',
    version: '',
    visibility: 'private',
    shared_with_company_ids: [],
    requires_acceptance: false,
  });
  const [editSaving, setEditSaving] = useState(false);

  // Upload form state
  const [uploadForm, setUploadForm] = useState<UploadFormData>(INITIAL_UPLOAD_FORM);
  // Synchronous re-entry lock for the upload submit. The `uploading` state
  // alone can't prevent a double-submit: `disabled={uploading}` only takes
  // effect after React re-renders, so on a slow upload an impatient user
  // clicking the button 2-3 times fires the handler 2-3 times before it
  // disables — creating duplicate documents. This ref blocks re-entry the
  // instant the first submit starts.
  const uploadInFlightRef = useRef(false);

  useEffect(() => {
    fetchData();
  }, [viewMode, pagination.currentPage, searchTerm, filters, viewKey, side]);

  useEffect(() => {
    if (uploadDialog.isOpen || showFilters || editDialog.isOpen) {
      fetchCategories();
    }
  }, [uploadDialog.isOpen, showFilters, editDialog.isOpen]);

  // CR-5328: keep the detail-modal's `document` prop in sync with the
  // refreshed list/folder data. After a successful version upload the
  // parent calls fetchData() to pull new metadata (size, version label),
  // but detailDoc was a stable snapshot so the modal's "Current" row
  // showed the pre-upload version. This effect re-points detailDoc at
  // the freshest copy whenever the list refetches.
  useEffect(() => {
    setDetailDoc(prev => {
      if (!prev) return prev;
      const fresh =
        documents.find(d => d.id === prev.id) ||
        categoriesWithDocs.flatMap(c => c.documents).find(d => d.id === prev.id);
      return fresh && fresh !== prev ? fresh : prev;
    });
  }, [documents, categoriesWithDocs]);

  const fetchCategories = async () => {
    try {
      // Folders power the Upload / Edit / Filter pickers, so EVERY folder
      // must be present — getAllCategories walks all paginated pages (the
      // list endpoint caps at 20/page by default), otherwise folders beyond
      // the first page are silently unselectable.
      setCategories(await documentsAPI.getAllCategories());
    } catch (err: unknown) {
      console.error('Error fetching categories:', err);
      toast.error('Failed to load categories');
    }
  };

  // Helper to apply filters to a document
  const matchesFilters = (doc: Document): boolean => {
    // Document type filter
    if (filters.documentType !== 'all' && doc.document_type !== filters.documentType) {
      return false;
    }
    // Category filter
    if (filters.category !== 'all') {
      const categoryId = parseInt(filters.category, 10);
      if (doc.category !== categoryId) {
        return false;
      }
    }
    // Visibility filter
    // CR-5327: Three visibility states mirror the upload form. A doc is
    // "shared" when it isn't public AND has at least one explicit share
    // (shared_with_company_ids is a non-empty array on the list
    // serializer). Strict private excludes shared docs.
    if (filters.visibility !== 'all') {
      const sharedCount = doc.shared_with_company_ids?.length ?? 0;
      if (filters.visibility === 'public' && !doc.is_public) return false;
      if (filters.visibility === 'private' && (doc.is_public || sharedCount > 0)) return false;
      if (filters.visibility === 'shared' && (doc.is_public || sharedCount === 0)) return false;
    }
    return true;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const viewParams = getViewQueryParams();

      if (viewMode === 'folder') {
        // Strip the view-context company/group/site params before calling
        // document endpoints. DjangoFilterBackend interprets `company=X` as
        // "Document.company_id == X" which is exact-match on the uploader's
        // company — documents uploaded by a supplier belong to the supplier,
        // not the viewing customer, so a customer looking at MAB saw 0 docs
        // even though the stats endpoint (which doesn't apply filter_queryset)
        // said 880. Scoping is handled by the backend's get_queryset based on
        // the caller's profile and effective company type; we don't need to
        // pass the top-level tenant picker to Documents.
        const { company: _c, group: _g, site: _s, ...documentParams } = viewParams as Record<string, string | number | boolean | undefined>;
        void _c; void _g; void _s;
        const [categoriesResponse, statsResponse] = await Promise.all([
          documentsAPI.getDocumentsByCategory({ ...documentParams, side }),
          documentsAPI.getDocumentStats({ ...documentParams, side }),
        ]);

        let categories = categoriesResponse.data;

        // Filter by search term if provided
        if (searchTerm) {
          categories = categories.map(cat => ({
            ...cat,
            documents: cat.documents.filter(doc =>
              doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
              doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              doc.tags?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          })).filter(cat => cat.documents.length > 0);
        }

        // Apply filters
        const hasActiveFilters = filters.documentType !== 'all' ||
                                  filters.category !== 'all' ||
                                  filters.visibility !== 'all';
        if (hasActiveFilters) {
          categories = categories.map(cat => ({
            ...cat,
            documents: cat.documents.filter(matchesFilters)
          })).filter(cat => cat.documents.length > 0);

          // If filtering by category, only show that category
          if (filters.category !== 'all') {
            const categoryId = parseInt(filters.category, 10);
            categories = categories.filter(cat => cat.id === categoryId);
          }
        }

        setCategoriesWithDocs(categories);
        setStats(statsResponse.data);
      } else {
        // Build API params with filters
        const params: Record<string, string | number | boolean | undefined> = {
          page: pagination.currentPage,
          page_size: pagination.pageSize,
          search: searchTerm || undefined,
          ordering: '-uploaded_at',
          side,
        };

        // Add filter params
        if (filters.documentType !== 'all') {
          params['document_type'] = filters.documentType;
        }
        if (filters.category !== 'all') {
          params['category'] = parseInt(filters.category, 10);
        }
        // CR-5327: Forward the three-way visibility state as a single
        // query param so the backend can apply the shared-docs filter
        // (M2M presence check) that can't be expressed via is_public
        // alone. Backend interprets visibility=public/private/shared.
        if (filters.visibility !== 'all') {
          params['visibility'] = filters.visibility;
        }

        // Intentionally NOT forwarding the view-context site/company/group
        // params — see the folder-mode block above. Document ownership is
        // per-uploader and doesn't align with the top-level tenant picker.
        const [docsResponse, statsResponse] = await Promise.all([
          documentsAPI.getDocuments(params),
          documentsAPI.getDocumentStats({ side }),
        ]);

        setDocuments(docsResponse.data.results);
        pagination.setFromResponse(docsResponse.data.count, (docsResponse.data.results || []).length);
        setStats(statsResponse.data);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load documents'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    pagination.reset();
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleFilterChange = (key: keyof DocumentFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    pagination.reset();
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    pagination.reset();
  };

  const hasActiveFilters = filters.documentType !== 'all' ||
                           filters.category !== 'all' ||
                           filters.visibility !== 'all';

  const handleDownload = async (doc: Document) => {
    // CWE-601: a "url" document opens an external link from a trusted
    // Download action. Block dangerous schemes and require the user to
    // confirm the destination host before leaving the platform.
    if (doc.document_type === 'url') {
      const cfg = externalNavConfirmConfig(doc.external_url || '');
      if (!cfg) {
        toast.error('This document has an invalid or unsafe external URL.');
        return;
      }
      const ok = await confirm(cfg);
      if (!ok) return;
    }
    try {
      const response = await documentsAPI.downloadDocument(doc.id);
      if (!response.data.file_url) {
        toast.error('Failed to download document');
        return;
      }
      window.open(response.data.file_url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      console.error('Error downloading document:', err);
      toast.error('Failed to download document');
    }
  };

  // Delete document
  const handleDelete = async (doc: Document) => {
    const ok = await confirm({ title: 'Delete Document', description: `Are you sure you want to delete "${doc.title}"?`, confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) return;
    try {
      await documentsAPI.deleteDocument(doc.id);
      // CR-5377: close the detail modal if it was showing the deleted
      // doc — otherwise the user sees the success toast but the pop-up
      // is still rendering the now-deleted record, which reads as "did
      // it actually delete?" and provokes a duplicate-click.
      setDetailDoc(prev => (prev?.id === doc.id ? null : prev));
      toast.success('Document deleted successfully');
      await fetchData();
    } catch (err: unknown) {
      console.error('Error deleting document:', err);
      toast.error(getApiErrorMessage(err, 'Failed to delete document'));
    }
  };

  // Preview document (opens inline in new tab).
  // CR-5331: Use the same HMAC-signed URL flow as Download — the
  // permanent JWT must not ride in the URL (CWE-200: leaks via
  // browser history / Heroku log drains / Referer headers), and the
  // backend's preview endpoint rejects `?token=<jwt>` because it only
  // honours `?u&exp&sig` minted by sign_preview(). The download
  // endpoint already returns the correctly-signed file_url.
  const handlePreview = async (doc: Document) => {
    if (doc.document_type === 'url') {
      // CWE-601: same external-navigation guard as handleDownload.
      const externalUrl = doc.external_url || '';
      const cfg = externalNavConfirmConfig(externalUrl);
      if (!cfg) {
        toast.error('This document has an invalid or unsafe external URL.');
        return;
      }
      const ok = await confirm(cfg);
      if (!ok) return;
      window.open(externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const response = await documentsAPI.downloadDocument(doc.id);
      const fileUrl = response.data.file_url;
      if (!fileUrl) {
        toast.error('Failed to preview document');
        return;
      }
      // PDFs render inline via pdf.js (scripting disabled), so we never hand a
      // raw PDF to the browser's native viewer — the backend can keep forcing
      // download on direct navigation as defence-in-depth (CWE-79).
      const ext = (doc.file_extension || '').toLowerCase()
        || (doc.file_url || '').split('?')[0].split('.').pop()?.toLowerCase() || '';
      const isPdf = ext === 'pdf' || (doc.mime_type || '') === 'application/pdf';
      if (isPdf) {
        setPdfPreview({ open: true, fileUrl, title: doc.title || 'Preview' });
        return;
      }
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      console.error('Error previewing document:', err);
      toast.error('Failed to preview document');
    }
  };

  const closePdfPreview = () => setPdfPreview({ open: false, fileUrl: null, title: '' });

  // CR-5330: Open the details popup for a document. Stash the row so
  // the modal has a stable snapshot even if fetchData() refreshes the
  // list mid-view.
  const handleOpenDetail = (doc: Document) => {
    setDetailDoc(doc);
    detailDialog.open();
  };

  const handleDetailDialogClose = () => {
    detailDialog.close();
    setDetailDoc(null);
  };

  // Edit document
  const handleEdit = (doc: Document) => {
    // CR-5325: Derive the three-way visibility from the doc's flags.
    // is_public=true → public; is_public=false + non-empty share list →
    // shared; otherwise private. The shared list comes from the list
    // serializer's shared_with_company_ids field.
    const sharedIds = doc.shared_with_company_ids ?? [];
    const visibility: DocumentVisibility = doc.is_public
      ? 'public'
      : sharedIds.length > 0
        ? 'shared'
        : 'private';
    setEditForm({
      id: doc.id,
      title: doc.title,
      description: doc.description || '',
      category: doc.category?.toString() || '',
      tags: doc.tags || '',
      version: doc.version || '',
      visibility,
      shared_with_company_ids: sharedIds,
      requires_acceptance: !!doc.requires_acceptance,
    });
    editDialog.open();
  };

  const handleEditDialogClose = () => {
    editDialog.close();
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm.title.trim()) {
      toast.error('Please enter a document title');
      return;
    }
    try {
      setEditSaving(true);
      // CR-5325: Send the full upload-shaped payload on edit. JSON keeps
      // shared_with_company_ids as a real array (FormData has no clean
      // way to send an empty list). Visibility maps to:
      //   public  → is_public=true,  shares=[]
      //   shared  → is_public=false, shares=[…]
      //   private → is_public=false, shares=[]
      const payload: Record<string, unknown> = {
        title: editForm.title,
        description: editForm.description,
        tags: editForm.tags,
        version: editForm.version,
        category: editForm.category ? parseInt(editForm.category, 10) : null,
        is_public: editForm.visibility === 'public',
        shared_with_company_ids: editForm.visibility === 'shared'
          ? editForm.shared_with_company_ids
          : [],
        // CR-5329
        requires_acceptance: editForm.requires_acceptance,
      };
      await documentsAPI.updateDocument(editForm.id, payload as Partial<Document>);
      handleEditDialogClose();
      toast.success('Document updated successfully');
      await fetchData();
    } catch (err: unknown) {
      console.error('Error updating document:', err);
      toast.error(getApiErrorMessage(err, 'Failed to update document'));
    } finally {
      setEditSaving(false);
    }
  };

  // Open the upload dialog, optionally pre-selecting a target folder so a
  // user who drilled into a folder/subfolder doesn't have to re-find it in
  // the picker. Resets the rest of the form to a clean state on each open.
  const handleUploadDialogOpen = (presetCategoryId?: number | null) => {
    setUploadForm({
      ...INITIAL_UPLOAD_FORM,
      category: presetCategoryId != null ? String(presetCategoryId) : '',
    });
    uploadDialog.open();
  };

  const handleUploadDialogClose = () => {
    uploadDialog.close();
    // Reset form
    setUploadForm(INITIAL_UPLOAD_FORM);
  };

  // Allowed file extensions (must match server-side)
  const ALLOWED_EXTENSIONS = [
    'pdf', 'doc', 'docx', 'xls', 'xlsx', 'xlsb', 'ppt', 'pptx',
    'txt', 'csv', 'zip', 'rar', '7z',
    'jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp',
  ];

  // Maximum file size (50MB - must match server-side documents/services.py)
  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  // Maximum number of files in a single multi-file upload. Keeps a stray
  // "select 500 files" from firing hundreds of sequential POST requests.
  const MAX_FILES_PER_UPLOAD = 20;

  const validateFile = (file: File): string | null => {
    // Check file extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      return `Invalid file type. Allowed file types: ${ALLOWED_EXTENSIONS.join(', ')}.`;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds maximum allowed size of ${MAX_FILE_SIZE / 1024 / 1024}MB.`;
    }

    return null;
  };

  // Customer review 11 Sep: fed by the dialog's FileDropZone (drop or pick).
  const handleFileChange = (picked: File[]) => {
    if (picked.length === 0) {
      setUploadForm({ ...uploadForm, files: [] });
      return;
    }
    if (picked.length > MAX_FILES_PER_UPLOAD) {
      toast.error(`You can upload up to ${MAX_FILES_PER_UPLOAD} files at a time. You selected ${picked.length}.`);
      return;
    }
    // Validate every picked file; surface the first failure with a toast
    // and abort so the user can re-pick — partial selection is more
    // confusing than no selection.
    for (const f of picked) {
      const err = validateFile(f);
      if (err) {
        toast.error(`${f.name}: ${err}`);
        return;
      }
    }
    // Auto-fill title from the first file's name when the user hasn't
    // typed one yet — preserves the previous single-file behaviour.
    const nextTitle = uploadForm.title.trim()
      ? uploadForm.title
      : picked[0].name.replace(/\.[^.]+$/, '');
    setUploadForm({ ...uploadForm, files: picked, title: nextTitle });
  };

  // CR-5324: Page-level drag-and-drop. Reset the form to upload-file
  // mode with the dropped file pre-selected, then open the dialog so
  // the user can fill in title/folder/visibility before submitting.
  // Validation reuses the same rules as the file picker — invalid drops
  // surface a toast and do not open the dialog.
  const handleDroppedFile = (fileOrFiles: File | File[], presetCategoryId?: number | null) => {
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    if (files.length > MAX_FILES_PER_UPLOAD) {
      toast.error(`You can upload up to ${MAX_FILES_PER_UPLOAD} files at a time. You dropped ${files.length}.`);
      return;
    }
    for (const f of files) {
      const error = validateFile(f);
      if (error) {
        toast.error(`${f.name}: ${error}`);
        return;
      }
    }
    setUploadForm({
      ...INITIAL_UPLOAD_FORM,
      files,
      document_type: 'file',
      // Pre-select the folder the user is currently drilled into so a drop
      // lands in that folder rather than Unassigned. Mirrors the "Upload
      // here" button (handleUploadDialogOpen). Falls back to no folder when
      // dropped at the root grid (no folder context).
      category: presetCategoryId != null ? String(presetCategoryId) : '',
      // Use the first file's name as the default title; ignored at submit
      // when files.length > 1 (each file gets its own title from its name).
      title: files[0].name.replace(/\.[^.]+$/, ''),
    });
    uploadDialog.open();
  };

  // --- Multi-select + bulk permissions -------------------------------
  // Lets a user tick several documents in a folder and set visibility /
  // share / read-&-accept on all of them at once, instead of editing each
  // one. Selection is keyed by document id; the bulk dialog reuses the same
  // VisibilityField as the upload/edit forms.
  const [selectedDocIds, setSelectedDocIds] = useState<number[]>([]);
  const bulkPermsDialog = useModal();
  const [bulkPermsSaving, setBulkPermsSaving] = useState(false);
  const [bulkPermsForm, setBulkPermsForm] = useState<{
    visibility: DocumentVisibility;
    shared_with_company_ids: number[];
    requires_acceptance: boolean;
  }>({ visibility: 'private', shared_with_company_ids: [], requires_acceptance: false });

  const toggleDocSelected = (id: number) => {
    setSelectedDocIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  };

  // Replace the selection with the given ids (used by the "select all"
  // header checkbox to select every document in the open folder at once).
  const selectAllDocs = (ids: number[]) => setSelectedDocIds(ids);

  const clearSelectedDocs = () => setSelectedDocIds([]);

  const handleOpenBulkPerms = () => {
    // Reset to a clean private default each open — bulk permissions are an
    // explicit set, not a merge of the selected docs' existing states.
    setBulkPermsForm({ visibility: 'private', shared_with_company_ids: [], requires_acceptance: false });
    bulkPermsDialog.open();
  };

  const handleBulkPermsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedDocIds.length === 0) return;
    try {
      setBulkPermsSaving(true);
      const payload: Record<string, unknown> = {
        is_public: bulkPermsForm.visibility === 'public',
        shared_with_company_ids:
          bulkPermsForm.visibility === 'shared' ? bulkPermsForm.shared_with_company_ids : [],
        requires_acceptance: bulkPermsForm.requires_acceptance,
      };
      const results = await Promise.allSettled(
        selectedDocIds.map(id => documentsAPI.updateDocument(id, payload as Partial<Document>)),
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      if (failed === 0) {
        toast.success(`Updated permissions on ${ok} document${ok === 1 ? '' : 's'}`);
      } else if (ok === 0) {
        toast.error(`Failed to update permissions on all ${failed} documents`);
      } else {
        toast.error(`Updated ${ok}, failed ${failed}`);
      }
      bulkPermsDialog.close();
      clearSelectedDocs();
      await fetchData();
    } catch (err: unknown) {
      console.error('Error updating permissions:', err);
      toast.error(getApiErrorMessage(err, 'Failed to update permissions'));
    } finally {
      setBulkPermsSaving(false);
    }
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Block a concurrent/double submit before any async work begins. Without
    // this, rapid clicks on a slow upload create duplicate documents.
    if (uploadInFlightRef.current) return;

    // Validation
    if (uploadForm.document_type === 'file' && uploadForm.files.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    if (uploadForm.document_type === 'url' && !uploadForm.external_url.trim()) {
      toast.error('Please enter an external URL');
      return;
    }

    // Title is optional. Multi-file uploads always derive the title from
    // each file's name. For a single file or URL, fall back to the file
    // name (or "External Link") when the user left the Title blank — so the
    // field never blocks an upload.
    const fallbackTitle =
      uploadForm.document_type === 'url'
        ? 'External Link'
        : uploadForm.files[0]?.name.replace(/\.[^.]+$/, '') || 'Untitled';
    const effectiveTitle = uploadForm.title.trim() || fallbackTitle;

    try {
      uploadInFlightRef.current = true;
      setUploading(true);

      // Build a re-usable FormData factory so each file in a multi-file
      // upload gets the same metadata block. URL uploads use a single
      // call.
      const buildFormData = (file: File | null, titleOverride?: string) => {
        const fd = new FormData();
        fd.append('title', titleOverride ?? uploadForm.title);
        fd.append('description', uploadForm.description);
        fd.append('document_type', uploadForm.document_type);
        // Visibility → backend fields:
        //   public → is_public=True (everyone), no shares
        //   shared → is_public=False + list of supplier company ids
        //   private → is_public=False, no shares (owning company only)
        fd.append('is_public', (uploadForm.visibility === 'public').toString());
        if (uploadForm.visibility === 'shared') {
          for (const cid of uploadForm.shared_with_company_ids) {
            fd.append('shared_with_company_ids', String(cid));
          }
        }
        if (uploadForm.category) fd.append('category', uploadForm.category);
        if (uploadForm.tags) fd.append('tags', uploadForm.tags);
        if (uploadForm.version) fd.append('version', uploadForm.version);
        fd.append('requires_acceptance', uploadForm.requires_acceptance.toString());
        if (file !== null) {
          fd.append('file', file);
        } else if (uploadForm.document_type === 'url') {
          fd.append('external_url', uploadForm.external_url);
        }
        return fd;
      };

      if (uploadForm.document_type === 'url') {
        await documentsAPI.createDocument(buildFormData(null, effectiveTitle));
        toast.success('Document uploaded successfully');
      } else if (uploadForm.files.length === 1) {
        await documentsAPI.createDocument(buildFormData(uploadForm.files[0], effectiveTitle));
        toast.success('Document uploaded successfully');
      } else {
        // Multi-file: upload sequentially so a 500 on file 7 doesn't
        // leave files 1-6 stranded with retries running in parallel.
        // Per-file title derived from the file name.
        let succeeded = 0;
        const failures: string[] = [];
        for (const f of uploadForm.files) {
          const perTitle = f.name.replace(/\.[^.]+$/, '');
          try {
            await documentsAPI.createDocument(buildFormData(f, perTitle));
            succeeded += 1;
          } catch (err: unknown) {
            console.error(`Error uploading ${f.name}:`, err);
            failures.push(f.name);
          }
        }
        if (failures.length === 0) {
          toast.success(`Uploaded ${succeeded} documents`);
        } else if (succeeded === 0) {
          toast.error(`All ${failures.length} uploads failed`);
        } else {
          toast.error(
            `Uploaded ${succeeded}, failed ${failures.length}: ${failures.slice(0, 3).join(', ')}${failures.length > 3 ? '…' : ''}`,
          );
        }
      }

      await fetchData(); // Refresh the document list
      handleUploadDialogClose();
    } catch (err: unknown) {
      console.error('Error uploading document:', err);
      toast.error(getApiErrorMessage(err, 'Failed to upload document'));
    } finally {
      setUploading(false);
      uploadInFlightRef.current = false;
    }
  };

  return {
    // View state
    viewMode,
    setViewMode,

    // Loading states
    loading,
    error,
    uploading,

    // Search
    searchTerm,
    handleSearchChange,

    // Filters
    showFilters,
    filters,
    hasActiveFilters,
    handleToggleFilters,
    handleFilterChange,
    handleClearFilters,

    // Data
    stats,
    categoriesWithDocs,
    documents,
    categories,
    pagination,

    // Upload dialog
    uploadDialog,
    uploadForm,
    setUploadForm,
    handleUploadDialogOpen,
    handleUploadDialogClose,
    handleFileChange,
    handleDroppedFile,
    handleUploadSubmit,

    // Actions
    fetchData,
    handleDownload,
    handleDelete,
    handleEdit,
    handlePreview,
    pdfPreview,
    closePdfPreview,

    // Edit dialog
    editDialog,
    editForm,
    setEditForm,
    handleEditDialogClose,
    handleEditSubmit,
    editSaving,

    // CR-5330: Detail dialog
    detailDialog,
    detailDoc,
    handleOpenDetail,
    handleDetailDialogClose,

    // Multi-select + bulk permissions
    selectedDocIds,
    toggleDocSelected,
    selectAllDocs,
    clearSelectedDocs,
    bulkPermsDialog,
    bulkPermsForm,
    setBulkPermsForm,
    bulkPermsSaving,
    handleOpenBulkPerms,
    handleBulkPermsSubmit,
  };
}
